// src/components/SearchFilterProjects.js

import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, ChevronUp } from 'lucide-react';

const SearchFilterProjects = ({ onSearch, onSort, searchValue = '' }) => {
  const [searchTerm, setSearchTerm] = useState(searchValue);
  const [sortOption, setSortOption] = useState('dateDesc');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update local state when the searchValue prop changes from parent
  useEffect(() => {
    setSearchTerm(searchValue);
  }, [searchValue]);

  // Define all possible sort options, including relevance
  const sortOptions = [
    { id: 'relevance', label: 'Best Match' },
    { id: 'dateDesc', label: 'Date Created (Newest)' },
    { id: 'dateAsc', label: 'Date Created (Oldest)' },
    { id: 'titleAsc', label: 'Title (A-Z)' },
    { id: 'titleDesc', label: 'Title (Z-A)' }
  ];

  // Conditionally filter sort options: only show "Best Match" when searching
  const availableSortOptions = searchTerm && searchTerm.trim()
    ? sortOptions
    : sortOptions.filter(option => option.id !== 'relevance');

  // Automatically switch to 'relevance' sort when user starts searching
  useEffect(() => {
    if (searchTerm && searchTerm.trim() && sortOption !== 'relevance') {
      setSortOption('relevance');
      onSort('relevance');
    }
    else if ((!searchTerm || !searchTerm.trim()) && sortOption === 'relevance') {
      setSortOption('dateDesc');
      onSort('dateDesc');
    }
  }, [searchTerm, sortOption, onSort]);

  // Close dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    onSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSortChange = (option) => {
    setSortOption(option);
    onSort(option);
    setIsDropdownOpen(false);
  };

  const currentSortLabel = sortOptions.find(option => option.id === sortOption)?.label || 'Date Created (Newest)';

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-4">
      {/* Search Field */}
      <div className="relative flex-grow">
        <div className="relative flex items-center">
          <Search className="absolute left-3 text-gray-500 w-5 h-5" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by project title or description"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black transition-shadow"
            aria-label="Search projects"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-100"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Sort Dropdown */}
      <div ref={dropdownRef} className="relative w-full sm:w-64">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center justify-between w-full px-4 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black transition-shadow"
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
        >
          <span>{currentSortLabel}</span>
          {isDropdownOpen ? (
            <ChevronUp className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {isDropdownOpen && (
          <ul
            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            {availableSortOptions.map((option) => (
              <li key={option.id}>
                <button
                  onClick={() => handleSortChange(option.id)}
                  className={`block w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors ${
                    sortOption === option.id
                      ? 'bg-gray-100 font-medium text-black'
                      : 'text-gray-700'
                  }`}
                  role="option"
                  aria-selected={sortOption === option.id}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SearchFilterProjects;